/**
 * Live policy updates (2026-10-22) - the browser end of the backend's socket (backend shared/realtime/).
 *
 * ONE connection per tab, kept at module level so it survives page changes (the navbar is mounted again on every
 * route). socket.io-client is loaded only when the connection is first opened, so it is not in the first page load.
 * Websocket only - no long-polling fallback, nothing is ever polled.
 *
 * What arrives is re-sent on `window` as POLICY_EVENT, so any page can listen without being wired to the socket:
 *   { type: "changed", policyId, kind, status, label }  refresh silently (everyone allowed to see the policy)
 *   { type: "notify", kind: "job" | "qr", ... }           the creator's toast / payment-QR dialog
 *   { type: "resync" }                                    the socket came back after a break - refresh once
 *
 * Who receives what is decided by the server; the login sent here is the same ClientId / UserId / UserType every REST
 * request sends (AxiosInterCeptor.js).
 */
export const POLICY_EVENT = "realtime:policy";

/** Where the socket lives: the API's own server. "https://x.com/api" -> { url: "https://x.com", path: "/socket.io" } */
export const realtimeTarget = (base = process.env.React_App_BaseUrl) => {
  try {
    const u = new URL(String(base || ""), window.location.origin);
    const prefix = u.pathname.replace(/\/+$/, "").replace(/\/api$/, "");
    return { url: u.origin, path: `${prefix}/socket.io` };
  } catch (e) {
    return { url: window.location.origin, path: "/socket.io" };
  }
};

const login = () => ({
  clientId: sessionStorage.getItem("clientID") || "",
  userId: sessionStorage.getItem("UserId") || "",
  userType: sessionStorage.getItem("userType") || "",
});
const identityOf = (who) => `${who.userType}|${who.clientId}|${who.userId}`;

let socket = null;
let socketIdentity = "";
let opening = null;

const send = (detail) => window.dispatchEvent(new CustomEvent(POLICY_EVENT, { detail }));

/**
 * Open the connection for the signed-in login (does nothing without one, or when it is already open for the same login;
 * a different login closes the old one first).
 */
export async function connectRealtime({ loadClient = () => import("socket.io-client") } = {}) {
  const who = login();
  if (!sessionStorage.getItem("token") || !who.userId) return null;
  const identity = identityOf(who);
  if (socket && socketIdentity === identity) return socket;
  if (opening) return opening;
  if (socket) disconnectRealtime();

  opening = (async () => {
    const { io } = await loadClient();
    const { url, path } = realtimeTarget();
    const s = io(url, {
      path,
      transports: ["websocket"],
      auth: (cb) => cb(login()),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });
    let connectedBefore = false;
    s.on("connect", () => {
      if (connectedBefore) send({ type: "resync" });
      connectedBefore = true;
    });
    s.on("policy:changed", (m) => send({ ...(m || {}), type: "changed" }));
    s.on("policy:notify", (m) => send({ ...(m || {}), type: "notify" }));
    socket = s;
    socketIdentity = identity;
    return s;
  })();
  try {
    return await opening;
  } catch (e) {
    console.warn("Live updates are off - the socket could not be opened", e);
    return null;
  } finally {
    opening = null;
  }
}

/** Close the connection (sign out). */
export function disconnectRealtime() {
  if (socket) {
    try {
      socket.removeAllListeners();
      socket.disconnect();
    } catch (e) {
      /* already closed */
    }
  }
  socket = null;
  socketIdentity = "";
}

/** Listen to live policy events; returns the function that stops listening. */
export function onPolicyEvent(fn) {
  const handler = (e) => fn(e.detail || {});
  window.addEventListener(POLICY_EVENT, handler);
  return () => window.removeEventListener(POLICY_EVENT, handler);
}

/** Test seam: dispatch as if the socket had said it. */
export const emitPolicyEventForTest = send;
